import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import LiveEmptyState from "../components/LiveEmptyState";
import useLiveMode from "../hooks/useLiveMode";
import api from "../lib/api";
import { formatINR, formatPct, cplHealth, statusColor } from "../utils/format";
import { Search, Star, Phone } from "lucide-react";

const DATE_RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last_7d", label: "Last 7 days" },
  { key: "last_30d", label: "Last 30 days" },
  { key: "this_month", label: "This month" },
  { key: "all", label: "All time" },
];

export default function Campaigns() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [clientFilter, setClientFilter] = useState("");
  const [dateRange, setDateRange] = useState("last_7d");
  const [clients, setClients] = useState([]);
  const { isLive, isConnected } = useLiveMode();

  const load = () => {
    const params = { date_range: dateRange };
    if (clientFilter) params.client_id = clientFilter;
    api.get("/campaigns", { params }).then((r) => setRows(r.data));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateRange, clientFilter]);
  useEffect(() => { setStatusFilter(searchParams.get("status") || "all"); }, [searchParams]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (q && !r.name.toLowerCase().includes(q.toLowerCase()) && !r.client_name?.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [rows, q, statusFilter]);

  const rangeLabel = DATE_RANGES.find((d) => d.key === dateRange)?.label || dateRange;

  return (
    <Layout title="Campaigns" subtitle={`${filtered.length} of ${rows.length} campaigns · ${rangeLabel}`}>
      {isLive && rows.length === 0 ? (
        isConnected ? (
          <LiveEmptyState
            entity="campaigns"
            variant="module"
            title="No campaigns synced yet"
            message="Your Google Ads account is connected. Campaigns appear here as soon as the first sync cycle pulls metrics. Use Sync Now in Admin → Connectors to trigger immediately."
          />
        ) : (
          <LiveEmptyState entity="campaigns" />
        )
      ) : (
      <>
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="campaigns-filters">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
          <input
            data-testid="campaigns-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search campaigns or clients…"
            className="w-full h-10 pl-9 pr-3 border border-border bg-card rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
        </div>

        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          data-testid="campaigns-date-range"
          className="h-10 px-3 border border-border bg-card rounded-sm text-sm"
        >
          {DATE_RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>

        <select
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          data-testid="campaigns-client-filter"
          className="h-10 px-3 border border-border bg-card rounded-sm text-sm min-w-[160px]"
        >
          <option value="">All clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <div className="flex items-center gap-1 border border-border rounded-sm bg-card p-1">
          {["all", "active", "paused", "limited", "learning", "ended"].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold rounded-sm transition-colors ${
                statusFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="campaigns-table">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Status</th>
              <th>Client</th>
              <th className="text-right">Daily Budget</th>
              <th className="text-right">Spend</th>
              <th className="text-right">Impr.</th>
              <th className="text-right">Clicks</th>
              <th className="text-right">CTR</th>
              <th className="text-right">CPC</th>
              <th className="text-right">Conv.</th>
              <th className="text-right">Calls</th>
              <th className="text-right">Leads</th>
              <th className="text-right">CPL</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="cursor-pointer">
                <td>
                  <Link to={`/campaigns/${c.id}`} className="flex items-center gap-2 font-medium hover:text-primary">
                    {c.watchlist && <Star className="w-3 h-3 fill-current text-[hsl(38_92%_50%)]" />}
                    <span>{c.name}</span>
                  </Link>
                </td>
                <td><StatusBadge tone={statusColor(c.status)}>{c.status}</StatusBadge></td>
                <td className="text-muted-foreground">{c.client_name}</td>
                <td className="text-right">{formatINR(c.daily_budget)}</td>
                <td className="text-right font-medium">{formatINR(c.spend, { compact: true })}</td>
                <td className="text-right tabular">{c.impressions?.toLocaleString() || 0}</td>
                <td className="text-right tabular">{c.clicks?.toLocaleString() || 0}</td>
                <td className="text-right">{formatPct(c.ctr)}</td>
                <td className="text-right">{c.avg_cpc ? formatINR(c.avg_cpc) : "—"}</td>
                <td className="text-right tabular">{c.conversions ? c.conversions.toFixed(1) : "—"}</td>
                <td className="text-right tabular">
                  {c.phone_calls > 0 ? (
                    <span className="inline-flex items-center gap-1 text-[hsl(38_92%_45%)] font-semibold">
                      <Phone className="w-3 h-3" /> {c.phone_calls}
                    </span>
                  ) : "—"}
                </td>
                <td className="text-right font-medium">{c.leads}</td>
                <td className={`text-right ${
                  cplHealth(c.cpl) === "success" ? "text-[hsl(var(--success))]" :
                  cplHealth(c.cpl) === "warning" ? "text-[hsl(38_92%_30%)]" :
                  cplHealth(c.cpl) === "critical" ? "text-[hsl(var(--critical))]" : "text-muted-foreground"
                }`}>
                  {c.cpl ? formatINR(c.cpl) : "—"}
                </td>
                <td>
                  <Link to={`/campaigns/${c.id}`} className="text-xs text-primary hover:underline">Open →</Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="14" className="p-6 text-center text-sm text-muted-foreground">No campaigns match the current filters.</td></tr>}
          </tbody>
        </table>
      </div>
      </>
      )}
    </Layout>
  );
}
